import {createAnalysis} from 'analysis-tool/AnalysisActions';
import {CreateAnalysisParams, CreateAnalysisResponse} from 'analysis-tool/AnalysisDAO';
import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  STUDIES_GENERATE_ANALYSIS_STARTED_EVENT,
  STUDIES_GENERATE_ANALYSIS_ENDED_EVENT,
  composeStudiesGenerateAnalysisEventMetadata,
} from 'performance/processStudiesEvents';

interface CreateAnalysisForStudyMetadata {
  actionOrigin: ActionOrigin;
  studyType: StudyType | null;
  procedureType?: ProcedureType | null;
  sourceObjectIds?: Array<string>;
}

/**
 * Create analysis for study with perf metadata and server-side analytics enrichment.
 *
 * @param data Data for create analysis
 * @param metadata Action origin, study type, optional procedure type, and optional source object IDs
 * for metrics and analytics
 * @param withBackgroundSeedData Whether to seed data in the background (progress toast) rather than
 * navigating to the analysis. Defaults to false.
 */
export async function createAnalysisForStudy(
  data: CreateAnalysisParams,
  metadata: CreateAnalysisForStudyMetadata,
  withBackgroundSeedData: boolean = false
): Promise<CreateAnalysisResponse> {
  logProfilerTimingEvent(STUDIES_GENERATE_ANALYSIS_STARTED_EVENT);
  const analyticsMetadata: AnalyticsEventMetadata = {
    actionOrigin: metadata.actionOrigin,
    studyType: metadata.studyType,
    procedureType: metadata.procedureType ?? null,
    sourceObjectIds: metadata.sourceObjectIds ?? null,
  };
  const analysisResults = await createAnalysis(data, undefined, withBackgroundSeedData, analyticsMetadata);
  const eventMetadata = composeStudiesGenerateAnalysisEventMetadata(
    metadata.actionOrigin,
    metadata.studyType
  );
  logProfilerTimingEvent(STUDIES_GENERATE_ANALYSIS_ENDED_EVENT, eventMetadata);
  return analysisResults;
}
